import { useEffect, useState } from "react";

const STORAGE_KEY = "fitsme_referral_code";

/**
 * Captures `?r=<code>` from the URL on mount, persists to localStorage so it
 * survives page navigation/refresh, and exposes a tiny API to consumers.
 *
 *   const { code, referrer, clear } = useReferralCode();
 *
 * - `code`: the referral code currently active (string | "")
 * - `referrer`: { first_name, referrals_count } | null — auto-fetched once
 * - `clear()`: wipes the stored code (call after successful signup so a refresh
 *              doesn't re-trigger the "Sarah invited you" banner)
 */
export const useReferralCode = () => {
    const [code, setCode] = useState(() => {
        try {
            // URL takes precedence over storage so a fresh share-link always wins.
            const url = new URL(window.location.href);
            const param = (url.searchParams.get("r") || "").trim().toUpperCase();
            if (param) {
                localStorage.setItem(STORAGE_KEY, param);
                return param;
            }
            return localStorage.getItem(STORAGE_KEY) || "";
        } catch {
            return "";
        }
    });
    const [referrer, setReferrer] = useState(null);

    useEffect(() => {
        if (!code) return;
        let cancelled = false;
        (async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL;
                const res = await fetch(`${base}/api/waitlist/referrer/${encodeURIComponent(code)}`);
                if (!res.ok) {
                    // Unknown code → silently drop it. Don't show a misleading banner.
                    if (!cancelled) { setCode(""); localStorage.removeItem(STORAGE_KEY); }
                    return;
                }
                const data = await res.json();
                if (!cancelled) setReferrer(data);
            } catch {
                // Network blip — keep the code, don't crash.
            }
        })();
        return () => { cancelled = true; };
    }, [code]);

    const clear = () => {
        try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignored */ }
        setCode("");
        setReferrer(null);
    };

    return { code, referrer, clear };
};

/** Build a share URL for a given referral code. */
export const buildShareUrl = (code) => {
    if (!code) return "";
    try {
        const u = new URL(window.location.origin);
        u.searchParams.set("r", code);
        return u.toString();
    } catch {
        return `${window.location.origin}/?r=${encodeURIComponent(code)}`;
    }
};
